import { lazy, Suspense, useState, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuthErrorHandler } from "@/hooks/useAuthErrorHandler";
import Footer from "@/components/Footer";

// Lazy load wallet context to avoid React instance conflicts
const WalletContextProvider = lazy(() => 
  import("@/contexts/WalletContext").then(m => ({ default: m.WalletContextProvider }))
);

// Lazy load page components for code splitting
const Landing = lazy(() => import("./pages/Landing"));
const Index = lazy(() => import("./pages/Index"));
const Auth = lazy(() => import("./pages/Auth"));
const SignalHistory = lazy(() => import("./pages/SignalHistory"));
const Plans = lazy(() => import("./pages/Plans"));
const Subscription = lazy(() => import("./pages/Subscription"));
const ReferralProgram = lazy(() => import("./pages/ReferralProgram"));
const ReferralRedirect = lazy(() => import("./pages/ReferralRedirect"));
const Admin = lazy(() => import("./pages/Admin"));
const MarketNews = lazy(() => import("./pages/MarketNews"));
const MarketShock = lazy(() => import("./pages/MarketShock"));
const Chart = lazy(() => import("./pages/Chart"));
const Help = lazy(() => import("./pages/Help"));
const FAQ = lazy(() => import("./pages/FAQ"));
const Documentation = lazy(() => import("./pages/Documentation"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const TrainAI = lazy(() => import("./pages/TrainAI"));
const AuTrade = lazy(() => import("./pages/AuTrade"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const AppContent = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const location = useLocation();
  
  // Global auth error handling
  useAuthErrorHandler();
  
  // Hide footer on full-screen pages
  const hideFooter = location.pathname === '/chart';

  useEffect(() => {
    // Check initial auth state
    const checkSession = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        setIsAuthenticated(!!session);
      } catch (error) {
        console.error('Session check error:', error);
        setIsAuthenticated(false);
      } finally {
        setSessionChecked(true);
      }
    };
    
    checkSession();

    // Listen for auth changes including token refresh
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user?.id);
      
      // Handle token refresh and session recovery
      if (event === 'TOKEN_REFRESHED' || event === 'SIGNED_IN') {
        setIsAuthenticated(true);
        setSessionChecked(true);
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        setSessionChecked(true);
      } else {
        setIsAuthenticated(!!session);
        setSessionChecked(true);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <div className="flex flex-col min-h-screen">
        <div className="flex-1">
          <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-pulse">Loading...</div></div>}>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/dashboard" element={<Index />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/history" element={<SignalHistory />} />
              <Route path="/plans" element={<Plans />} />
              <Route path="/subscription" element={<Subscription />} />
              <Route path="/referral" element={<ReferralProgram />} />
              <Route path="/ref/:userId" element={<ReferralRedirect />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/news" element={<MarketNews />} />
              <Route path="/shock" element={<MarketShock />} />
              <Route path="/chart" element={<Chart />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/documentation" element={<Documentation />} />
              <Route path="/help" element={<Help />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsOfService />} />
              <Route path="/train-ai" element={<TrainAI />} />
              <Route path="/au-trade" element={<AuTrade />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </div>
        {!hideFooter && <Footer />}
      </div>
    </TooltipProvider>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-pulse">Loading...</div></div>}>
        <WalletContextProvider>
          <BrowserRouter>
            <AppContent />
          </BrowserRouter>
        </WalletContextProvider>
      </Suspense>
    </QueryClientProvider>
  );
};

export default App;
